import React, { useState } from 'react';
import c2Service from '../../../services/c2Service';
import './InfraDeployPanel.css';

/**
 * Infrastructure Deployment Panel
 * Validates topology and deploys infrastructure in correct order
 */
const InfraDeployPanel = ({ nodes, links, projectId, onBack }) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [deploymentLogs, setDeploymentLogs] = useState([]);
  const [deploymentStatus, setDeploymentStatus] = useState({}); // nodeId -> status
  const [validationErrors, setValidationErrors] = useState([]);

  const validateTopology = () => {
    const errors = [];

    // Check for isolated nodes
    const nodeIds = new Set(nodes.map(n => n.id));
    const connectedNodes = new Set();

    links.forEach(link => {
      connectedNodes.add(typeof link.source === 'object' ? link.source.id : link.source);
      connectedNodes.add(typeof link.target === 'object' ? link.target.id : link.target);
    });

    const isolatedNodes = nodes.filter(n => !connectedNodes.has(n.id) && n.type !== 'c2');

    if (isolatedNodes.length > 0) {
      errors.push(`Isolated nodes detected: ${isolatedNodes.map(n => n.label).join(', ')}`);
    }

    // Check for listeners without redirectors (warning only)
    const listeners = nodes.filter(n => n.type === 'listener');
    const listenersWithRedirectors = new Set();

    links.forEach(link => {
      const sourceNode = nodes.find(n => n.id === (typeof link.source === 'object' ? link.source.id : link.source));
      const targetNode = nodes.find(n => n.id === (typeof link.target === 'object' ? link.target.id : link.target));

      if (sourceNode?.type === 'redirector' && targetNode?.type === 'listener') {
        listenersWithRedirectors.add(targetNode.id);
      }
    });

    const exposedListeners = listeners.filter(l => !listenersWithRedirectors.has(l.id));
    if (exposedListeners.length > 0) {
      errors.push(`Warning: Listeners without redirectors: ${exposedListeners.map(l => l.label).join(', ')}`);
    }

    // Check for agents without configuration
    const unconfiguredAgents = nodes.filter(n => n.type === 'agent' && !n.config.name);
    if (unconfiguredAgents.length > 0) {
      errors.push(`Unconfigured agents: ${unconfiguredAgents.map(n => n.label).join(', ')}`);
    }

    setValidationErrors(errors);
    return errors.filter(e => !e.startsWith('Warning:')).length === 0;
  };

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setDeploymentLogs(prev => [...prev, { timestamp, message, type }]);
  };

  const updateNodeStatus = (nodeId, status) => {
    setDeploymentStatus(prev => ({ ...prev, [nodeId]: status }));
  };

  const deployInfrastructure = async () => {
    if (!validateTopology()) {
      addLog('Validation failed. Fix errors before deploying.', 'error');
      return;
    }

    setIsDeploying(true);
    setDeploymentLogs([]);
    setDeploymentStatus({});

    addLog('Starting infrastructure deployment...');

    try {
      // Phase 1: Deploy listeners
      const listeners = nodes.filter(n => n.type === 'listener');

      for (const listener of listeners) {
        addLog(`Creating listener: ${listener.label}...`);
        updateNodeStatus(listener.id, 'deploying');

        try {
          const listenerData = {
            name: listener.config.name || listener.label,
            type: listener.config.protocol || 'https',
            config: {
              port: listener.config.port || 5000,
              bindAddress: '0.0.0.0',
              ...(listener.config.protocol === 'dns' && { dnsZone: listener.config.dnsZone })
            },
            autoStart: listener.config.autoStart || true,
            autoRestart: listener.config.autoRestart || true
          };

          const response = await c2Service.createListener(listenerData);

          if (response.data.success) {
            addLog(`✓ Listener ${listener.label} created successfully`, 'success');
            updateNodeStatus(listener.id, 'success');
          } else {
            throw new Error(response.data.error || 'Failed to create listener');
          }
        } catch (error) {
          addLog(`✗ Failed to create listener ${listener.label}: ${error.message}`, 'error');
          updateNodeStatus(listener.id, 'failed');
        }
      }

      // Phase 2: Deploy redirectors
      const redirectors = nodes.filter(n => n.type === 'redirector');

      for (const redirector of redirectors) {
        addLog(`Deploying redirector: ${redirector.label}...`);
        updateNodeStatus(redirector.id, 'deploying');

        try {
          const redirectorData = {
            name: redirector.config.name || redirector.label,
            hostname: redirector.config.hostname,
            type: redirector.config.type || 'nginx',
            ssh: {
              host: redirector.config.sshHost || redirector.config.hostname,
              username: redirector.config.sshUser || 'root',
              privateKeyPath: redirector.config.sshKeyPath
            },
            projectId: projectId
          };

          const response = await c2Service.deployRedirector(redirectorData);

          if (response.data.success) {
            addLog(`✓ Redirector ${redirector.label} deployed successfully`, 'success');
            updateNodeStatus(redirector.id, 'success');
          } else {
            throw new Error(response.data.error || 'Failed to deploy redirector');
          }
        } catch (error) {
          addLog(`✗ Failed to deploy redirector ${redirector.label}: ${error.message}`, 'error');
          updateNodeStatus(redirector.id, 'failed');
        }
      }

      // Phase 3: Build agents
      const agents = nodes.filter(n => n.type === 'agent');

      // Get redirector URLs for failover config
      const getRedirectorUrls = (agentNode) => {
        const redirectorIds = [];

        links.forEach(link => {
          const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
          const targetId = typeof link.target === 'object' ? link.target.id : link.target;

          if (sourceId === agentNode.id) {
            const targetNode = nodes.find(n => n.id === targetId);
            if (targetNode?.type === 'redirector') {
              redirectorIds.push(targetId);
            }
          }
        });

        return redirectorIds.map(id => {
          const node = nodes.find(n => n.id === id);
          return node.config.hostname;
        });
      };

      for (const agent of agents) {
        addLog(`Building agent: ${agent.label}...`);
        updateNodeStatus(agent.id, 'deploying');

        try {
          const redirectorUrls = getRedirectorUrls(agent);

          const buildConfig = {
            name: agent.config.name,
            platform: agent.config.os || 'windows',
            c2Url: 'https://app.purplehq.ch', // Main C2 URL
            protocol: 'https',
            sleepInterval: 60,
            jitter: 30,
            failoverStrategy: agent.config.failoverStrategy || 'sequential'
          };

          const buildData = {
            platform: agent.config.os || 'windows',
            config: buildConfig,
            projectId: projectId,
            redirectorIds: redirectorUrls
          };

          const response = await c2Service.buildPayloads(buildData.platform, 'all', buildData.config);

          if (response.data.success) {
            addLog(`✓ Agent ${agent.label} build started (${response.data.build.id})`, 'success');
            updateNodeStatus(agent.id, 'success');
          } else {
            throw new Error(response.data.error || 'Failed to build agent');
          }
        } catch (error) {
          addLog(`✗ Failed to build agent ${agent.label}: ${error.message}`, 'error');
          updateNodeStatus(agent.id, 'failed');
        }
      }

      addLog('Infrastructure deployment completed!', 'success');
    } catch (error) {
      addLog(`Deployment error: ${error.message}`, 'error');
    } finally {
      setIsDeploying(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'deploying':
        return '⏳';
      case 'success':
        return '✓';
      case 'failed':
        return '✗';
      default:
        return '•';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'deploying':
        return 'status-deploying';
      case 'success':
        return 'status-success';
      case 'failed':
        return 'status-failed';
      default:
        return '';
    }
  };

  return (
    <div className="infra-deploy-panel">
      <div className="deploy-header">
        <button onClick={onBack} className="btn-back">
          ← Back to Design
        </button>
        <h3>Deploy Infrastructure</h3>
      </div>

      <div className="deploy-body">
        <div className="deploy-validation">
          <h4>Pre-deployment Validation</h4>
          {validationErrors.length > 0 ? (
            <div className="validation-errors">
              {validationErrors.map((error, index) => (
                <div
                  key={index}
                  className={error.startsWith('Warning:') ? 'validation-warning' : 'validation-error'}
                >
                  {error}
                </div>
              ))}
            </div>
          ) : (
            <div className="validation-success">
              ✓ Topology validated successfully
            </div>
          )}
        </div>

        <div className="deploy-summary">
          <h4>Deployment Summary</h4>
          <div className="summary-items">
            {nodes.map(node => (
              <div key={node.id} className={`summary-item ${getStatusClass(deploymentStatus[node.id])}`}>
                <span className="summary-icon">{getStatusIcon(deploymentStatus[node.id])}</span>
                <span className="summary-label">{node.label}</span>
                <span className="summary-type">{node.type}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="deploy-logs">
          <h4>Deployment Logs</h4>
          <div className="logs-container">
            {deploymentLogs.length === 0 ? (
              <div className="logs-empty">No logs yet. Click "Deploy All" to begin.</div>
            ) : (
              deploymentLogs.map((log, index) => (
                <div key={index} className={`log-entry log-${log.type}`}>
                  <span className="log-timestamp">{log.timestamp}</span>
                  <span className="log-message">{log.message}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="deploy-footer">
        <button onClick={onBack} className="btn-secondary" disabled={isDeploying}>
          Cancel
        </button>
        <button
          onClick={deployInfrastructure}
          className="btn-deploy"
          disabled={isDeploying || validationErrors.filter(e => !e.startsWith('Warning:')).length > 0}
        >
          {isDeploying ? 'Deploying...' : 'Deploy All'}
        </button>
      </div>
    </div>
  );
};

export default InfraDeployPanel;
